/**
 * GameStatsPanel — the per-game stat tables and the post-game entry grid.
 * 2026-09-19 (per Gilles, Phase 7A).
 *
 * Shown on the game page. Reads the rollup the server computes from the
 * event log, so the numbers here and in season totals can never drift.
 *
 * Layout follows what Gilles liked about Mad Puck: dense tables grouped by
 * stat family rather than one wide grid, so no single table needs horizontal
 * scrolling on a phone, with a team total row under each.
 *
 * EDITING is a grid, not a tap-fest: post-game, a coach has a scoresheet and
 * wants to type numbers. Saving PUTs the whole grid, which the server
 * reconciles — submit twice and the totals are identical. Live tapping lives
 * on LiveTrackingPage instead, because that is a different job.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { BarChart3, Pencil, Check, X, Mic, AlertCircle, Trash2, Upload, ListChecks } from 'lucide-react';
import StatsEditor from './StatsEditor.jsx';
import GoalTimeline, { isGoalEvent } from './GoalTimeline.jsx';
import ClipButton from './ClipButton.jsx';
import { ReassignPicker } from './GoalTimeline.jsx';
import VoiceLogPanel from './VoiceLogPanel.jsx';
import toast from 'react-hot-toast';
import api from '../../services/api.js';
// 2026-09-22 (Phase 7C) — live updates from the hockeystats sidecar arrive
// on the team socket as GAME_STATS_GOAL / GAME_STATS_BOX / GAME_STATS_FINAL.
import { chatSocket } from '../../services/socket.js';
import { uploadRecording, uploadError, RECORDING_ACCEPT } from '../../utils/recordingUpload.js';
import { modelAlias } from '../../constants/pressBox.js';

const GROUP_LABEL = {
  scoring: 'Scoring', shots: 'Shots', faceoffs: 'Faceoffs',
  penalties: 'Penalties', physical: 'Hits, blocks and turnovers',
  goalie: 'Goaltending', stoppages: 'Stoppages',
};

export default function GameStatsPanel({ teamId, gameId, canEdit = false }) {
  const [data, setData]   = useState(null);
  const [loading, setL]   = useState(true);
  const [editing, setEd]  = useState(false);
  const [draft, setDraft] = useState({});
  const [saving, setSav]  = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/teams/${teamId}/game-events/${gameId}`);
      setData(d);
    } catch {
      setData(null);
    } finally { setL(false); }
  }, [teamId, gameId]);

  useEffect(() => { load(); }, [load]);

  // 2026-09-22 — voice stats arriving live. Reload the rollup on every event
  // for this game; keep the engine's running score for the strip below.
  const [live, setLive] = useState(null);   // { score:{us,them}, period, final }
  const [upload, setUpload] = useState(null);   // { pct, phase:'sending'|'processing', video }
  const [editor, setEditor] = useState(false);  // 2026-10-01 — the event-by-event editor (Game Day Stats)
  const [voiceLog, setVoiceLog] = useState(false);  // 2026-10-07 — voice AI troubleshooting log
  useEffect(() => {
    // 2026-09-23 — the socket only receives team broadcasts after JOIN_TEAM,
    // which until now only the chat page ever sent. Without it every live
    // goal was broadcast into an empty room and the page needed a refresh.
    chatSocket.joinTeam(teamId);
    const mine = (d) => d && String(d.gameId) === String(gameId);
    const u1 = chatSocket.on('GAME_STATS_GOAL', (d) => { if (!mine(d)) return; setLive(l => ({ ...(l || {}), score: d.score || l?.score, period: d.period ?? l?.period })); load(); });
    const u2 = chatSocket.on('GAME_STATS_BOX',  (d) => { if (!mine(d)) return; setLive(l => ({ ...(l || {}), score: d.score || l?.score, period: d.period ?? l?.period, liveFinal: !!d.liveFinal })); load(); });
    const u3 = chatSocket.on('GAME_STATS_FINAL',(d) => {
      if (!mine(d)) return;
      setLive(l => ({ ...(l || {}), score: d.score || l?.score, final: true, analysis: d.analysis || null }));
      // 2026-10-17 — say who checked them: the rules alone, or the stats AI.
      const a = d.analysis || {};
      const msg = a.mode === 'rules+llm' ? `Official stats are in — checked by ${modelAlias(a.model) || 'the stats AI'}`
        : (a.requested === 'ai' ? `Official stats are in — rules only: the stats AI was not used (${a.error || 'no answer'})` : 'Official stats are in — the live numbers were replaced');
      toast.success(msg, { duration: 7000 });
      load();
    });
    // 2026-10-17 — where the official pass is ("AI analysis 23/48 min").
    const u5 = chatSocket.on('GAME_STATS_PROGRESS', (d) => { if (!mine(d)) return; setUpload(u => ({ ...(u || { pct: 100 }), phase: 'processing', message: d.message || null })); });
    // 2026-10-01 — the pass (or a video's audio extraction) failed: say so
    // instead of leaving "Working out the stats…" up for ever.
    const u4 = chatSocket.on('GAME_STATS_ERROR',(d) => { if (!mine(d)) return; setUpload(null); toast.error(`Stats could not be worked out: ${d.error || 'unknown error'}`, { duration: 10000 }); });
    return () => { u1?.(); u2?.(); u3?.(); u4?.(); u5?.(); };
  }, [teamId, gameId, load]);

  // Group the team's tracked stats into the tables we render.
  const groups = useMemo(() => {
    if (!data) return [];
    const tracked = new Set(data.tracked || []);
    const out = new Map();
    for (const s of data.catalogue || []) {
      if (!tracked.has(s.code)) continue;
      if (!out.has(s.group)) out.set(s.group, []);
      out.get(s.group).push(s);
    }
    return [...out.entries()].map(([g, stats]) => ({ group: g, stats }));
  }, [data]);

  const startEdit = () => {
    const d = {};
    for (const p of data.players || []) d[p.userId] = { ...p.stats };
    setDraft(d);
    setEd(true);
  };

  const save = async () => {
    setSav(true);
    try {
      const entries = Object.entries(draft).map(([userId, stats]) => ({ userId, stats }));
      await api.put(`/teams/${teamId}/game-events/${gameId}/tally`, { entries });
      toast.success('Stats saved');
      setEd(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Could not save stats');
    } finally { setSav(false); }
  };

  // ── 2026-09-22 (per Gilles) — GAME RECAP: resolve what the voice engine
  // heard but could not pin on a player. Each item shows the sentence it
  // heard and when; the coach picks the player (and side, on pickup nights)
  // and confirms, or discards it.
  const [draftReview, setDraftReview] = useState({});     // eventId → { user_id, team_side }
  const [busyReview, setBusyReview] = useState({});
  const REASON = {
    unresolved_player: "Couldn't match the player to the roster",
    no_player_named:   'No player was named',
    no_side:           "Player isn't on a side tonight",
    low_confidence:    "The engine wasn't sure it heard this right",
    goalie_scored:     'Credited to the goalie — goalies rarely score; listen and make sure',
    goalie_faceoff:    "Faceoff credited to the goalie — goalies don't take faceoffs; listen, or confirm they played out",
    skater_save:       'Save credited to a skater — only the goalie makes saves; listen and check',
  };
  const fmtAt = (ms) => ms == null ? '' : `${Math.floor(ms / 60000)}:${String(Math.floor((ms % 60000) / 1000)).padStart(2, '0')}`;
  const resolveItem = async (item) => {
    const d = draftReview[item.id] || {};
    const meta = (data?.catalogue || []).find(c => c.code === item.type);
    if (meta && meta.scope !== 'team' && !d.user_id) return toast.error('Pick a player first');
    setBusyReview(b => ({ ...b, [item.id]: true }));
    try {
      await api.patch(`/teams/${teamId}/game-events/${gameId}/${item.id}`, {
        ...(d.user_id !== undefined ? { user_id: d.user_id } : {}),
        ...(d.team_side !== undefined ? { team_side: d.team_side } : {}),
        resolved: true,
      });
      toast.success('Fixed');
      await load();
    } catch (e) { toast.error(e.response?.data?.error || 'Could not save'); }
    finally { setBusyReview(b => ({ ...b, [item.id]: false })); }
  };
  const discardItem = async (item) => {
    setBusyReview(b => ({ ...b, [item.id]: true }));
    try { await api.delete(`/teams/${teamId}/game-events/${gameId}/${item.id}`); toast.success('Discarded'); await load(); }
    catch { toast.error('Could not discard'); }
    finally { setBusyReview(b => ({ ...b, [item.id]: false })); }
  };

  // ── 2026-09-30 — upload a recording made outside the app (voice memo,
  // a mic at the bench). Sent in ~5 MB parts (every hop caps a request at
  // 10 MB), then the server runs the official pass; GAME_STATS_FINAL above
  // announces the result. The team without a live tracker still gets stats.
  const fileRef = useRef(null);
  // 2026-10-01 (Game Day Stats) — video too. 2026-10-17: the upload itself
  // lives in utils/recordingUpload.js (Press Box uploads through it too).
  const uploadFile = async (file) => {
    if (!file) return;
    setUpload({ pct: 0, phase: 'sending' });
    try {
      const { video } = await uploadRecording({ teamId, gameId, file, onProgress: (p) => setUpload(u => ({ ...(u || {}), ...p })) });
      toast.success(video ? 'Video uploaded — the sound is being pulled out, then the stats are worked out. A few minutes.' : 'Recording uploaded — the stats will appear here in a few minutes', { duration: 8000 });
    } catch (e) {
      setUpload(null);
      toast.error(uploadError(e), { duration: 8000 });
      return;
    }
    if (fileRef.current) fileRef.current.value = '';
  };
  useEffect(() => {
    if (!upload || upload.phase !== 'processing') return undefined;
    // the OFFICIAL strip (live.final) is the "done" signal
    if (live?.final) { setUpload(null); }
    return undefined;
  }, [live?.final, upload]);

  if (loading) return null;
  if (!data) return null;

  const players = data.players || [];
  const hasAny = (data.eventCount || 0) > 0 || (data.reviewCount || 0) > 0;

  // Nothing recorded and nobody can edit — don't take up space.
  if (!hasAny && !canEdit) return null;

  const cell = { padding: '6px 8px', fontVariantNumeric: 'tabular-nums', textAlign: 'right' };
  const headCell = { ...cell, fontWeight: 700, fontSize: 11, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '0.04em' };

  return (
    <div className="rounded-xl mb-4" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', overflow: 'hidden' }}>
      <div className="flex items-center justify-between gap-2 px-3 py-2.5" style={{ borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
        <div className="flex items-center gap-2">
          <BarChart3 size={15} style={{ color: 'var(--ice)' }} />
          <span className="text-sm font-bold">Game stats</span>
        </div>
        {canEdit && !editing && (
          <div className="flex items-center gap-2" style={{ flexWrap: 'wrap' }}>
            <Link to={`/team/${teamId}/game/${gameId}/live`}
              className="flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-lg"
              style={{ color: 'var(--ice)', border: '1px solid var(--border)' }}>
              <Mic size={12} /> Track live
            </Link>
            <input ref={fileRef} type="file" accept={RECORDING_ACCEPT} style={{ display: 'none' }}
              onChange={(e) => uploadFile(e.target.files?.[0])} />
            <button type="button" onClick={() => fileRef.current?.click()} disabled={!!upload}
              title="Recorded the game on a voice memo app, or filmed it? Upload the audio or video file and the stats are worked out from the narration."
              className="flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-lg"
              style={{ color: 'var(--text-2)', background: 'none', border: '1px solid var(--border)', cursor: upload ? 'progress' : 'pointer' }}>
              <Upload size={12} /> {upload ? (upload.phase === 'sending' ? `Uploading ${upload.pct}%` : (upload.message ? `Stats: ${upload.message}…` : (upload.video ? 'Pulling the sound out, then the stats…' : 'Working out the stats…'))) : 'Upload recording'}
            </button>
            <button type="button" onClick={() => setVoiceLog(v => !v)}
              title="Every step the voice AI took for this game: audio received and sent, what the engine heard, who each goal was tagged on, and where anything got lost."
              className="flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-lg"
              style={{ color: voiceLog ? 'var(--ice)' : 'var(--text-2)', background: 'none', border: `1px solid ${voiceLog ? 'var(--ice)' : 'var(--border)'}`, cursor: 'pointer' }}>
              🎙 {voiceLog ? 'Hide log' : 'Voice log'}
            </button>
            <button type="button" onClick={() => setEditor(v => !v)}
              title="Every goal, assist, shot and penalty, one by one — fix the player, the period or the stat, discard mistakes, add what was missed, with the transcript beside it."
              className="flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-lg"
              style={{ color: editor ? 'var(--ice)' : 'var(--text-2)', background: 'none', border: `1px solid ${editor ? 'var(--ice)' : 'var(--border)'}`, cursor: 'pointer' }}>
              <ListChecks size={12} /> {editor ? 'Done' : 'Fix events'}
            </button>
            {!editor && (
            <button type="button" onClick={startEdit}
              className="flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-lg"
              style={{ color: 'var(--text-2)', background: 'none', border: '1px solid var(--border)', cursor: 'pointer' }}>
              <Pencil size={12} /> Enter stats
            </button>
            )}
          </div>
        )}
        {editing && (
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => setEd(false)} disabled={saving}
              className="flex items-center gap-1 text-xs px-2 py-1 rounded-lg"
              style={{ color: 'var(--text-2)', background: 'none', border: '1px solid var(--border)', cursor: 'pointer' }}>
              <X size={12} /> Cancel
            </button>
            <button type="button" onClick={save} disabled={saving}
              className="flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-lg btn-ice">
              <Check size={12} /> {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        )}
      </div>

      {live?.score && (
        <div className="flex items-center gap-3 px-3 py-2 text-sm" style={{ borderBottom: '1px solid var(--border)', background: live.final ? 'rgba(0,232,122,0.06)' : 'rgba(255,59,59,0.06)' }}>
          {!live.final && <span className="flex items-center gap-1 text-xs font-bold" style={{ color: 'var(--puck, #ff3b3b)' }}>● LIVE</span>}
          {live.final && <span className="text-xs font-bold" style={{ color: 'var(--goal, #00e87a)' }}>OFFICIAL{live.analysis?.mode === 'rules+llm' ? ' · AI-CHECKED' : ''}</span>}
          <span className="font-bold" style={{ fontVariantNumeric: 'tabular-nums', fontSize: 16 }}>{live.score.us} – {live.score.them}</span>
          {live.period != null && <span className="text-xs" style={{ color: 'var(--text-3)' }}>P{live.period}</span>}
          <span className="text-xs" style={{ color: 'var(--text-3)' }}>from the voice tracker</span>
        </div>
      )}

      {voiceLog && canEdit && <VoiceLogPanel teamId={teamId} gameId={gameId} />}
      {/* 2026-10-07 — the goals in order: who scored, who assisted, voice or by hand */}
      {!editor && !editing && (data.events || []).some(isGoalEvent) && (
        <div className="px-3 py-2" style={{ borderBottom: '1px solid var(--border)' }}>
          <div className="text-xs font-bold mb-1" style={{ color: 'var(--text-2)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Goals</div>
          <GoalTimeline data={data} opponent={data.opponent} teamId={teamId} gameId={gameId} canEdit={canEdit} onChanged={load} />
        </div>
      )}
      {editor && canEdit && (
        <StatsEditor teamId={teamId} gameId={gameId} data={data} onChange={load} />
      )}
      {canEdit && (data.review || []).length > 0 && !editing && !editor && (
        <div style={{ borderBottom: '1px solid var(--border)', background: 'var(--surface-2)', borderTop: '2px solid var(--puck-a20, rgba(255,149,0,0.25))' }}>
          <div className="flex items-center gap-2 px-3 pt-3 pb-1">
            <AlertCircle size={14} style={{ color: 'var(--puck, #ff9500)' }} />
            <span className="text-sm font-bold">To check — {data.review.length} unfinished call{data.review.length === 1 ? '' : 's'}</span>
            <span className="text-xs" style={{ color: 'var(--text-3)' }}>(separate from the goals above)</span>
          </div>
          <div className="px-3 pb-1 text-xs" style={{ color: 'var(--text-2)' }}>
            The voice tracker heard these but couldn't finish them. They count for the team, not for a player, until you fix them.
          </div>
          {data.review.map(item => {
            const meta = (data.catalogue || []).find(c => c.code === item.type);
            const d = draftReview[item.id] || {};
            const busy = !!busyReview[item.id];
            return (
              <div key={item.id} className="px-3 py-2.5" style={{ borderTop: '1px solid var(--border)' }}>
                <div className="flex items-center gap-2 flex-wrap text-xs" style={{ color: 'var(--text-2)' }}>
                  <span className="font-bold" style={{ color: 'var(--text)' }}>{meta?.label || item.type}</span>
                  {item.atMs != null && <span>at {fmtAt(item.atMs)}</span>}
                  {item.period != null && <span>· P{item.period}</span>}
                  {item.jersey && <span>· heard #{item.jersey}</span>}
                  <span style={{ color: 'var(--puck, #ff9500)' }}>· {REASON[item.reason] || item.reason}</span>
                  <ClipButton teamId={teamId} recordingId={item.recordingId} offsetMs={item.atMs} />
                </div>
                {item.said && (
                  <div className="text-xs mt-1" style={{ fontStyle: 'italic', color: 'var(--text-2)' }}>💬 “{item.said}”</div>
                )}
                <div className="flex items-center gap-2 flex-wrap mt-2">
                  <select value={d.user_id ?? ''} disabled={busy}
                    onChange={e => setDraftReview(r => ({ ...r, [item.id]: { ...(r[item.id] || {}), user_id: e.target.value || null } }))}
                    className="input-field" style={{ padding: '4px 8px', fontSize: 12, maxWidth: 220 }}>
                    <option value="">Who was it?</option>
                    {(data.roster || []).map(p => (
                      <option key={p.userId} value={p.userId}>{p.name}{p.jerseyNumber != null ? ` #${p.jerseyNumber}` : ''}</option>
                    ))}
                  </select>
                  <select value={d.team_side ?? item.side ?? ''} disabled={busy}
                    onChange={e => setDraftReview(r => ({ ...r, [item.id]: { ...(r[item.id] || {}), team_side: e.target.value || null } }))}
                    className="input-field" style={{ padding: '4px 8px', fontSize: 12, width: 100 }}>
                    <option value="">Side</option>
                    <option value="home">Home</option>
                    <option value="away">Away</option>
                  </select>
                  <button type="button" onClick={() => resolveItem(item)} disabled={busy}
                    className="flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-lg btn-ice">
                    <Check size={12} /> Fix
                  </button>
                  <button type="button" onClick={() => discardItem(item)} disabled={busy}
                    className="flex items-center gap-1 text-xs px-2 py-1 rounded-lg"
                    style={{ color: 'var(--text-2)', background: 'none', border: '1px solid var(--border)', cursor: 'pointer' }}>
                    <Trash2 size={12} /> Didn't happen
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {!canEdit && (data.reviewCount || 0) > 0 && (
        <div className="px-3 py-2 text-xs" style={{ color: 'var(--text-3)', borderBottom: '1px solid var(--border)' }}>
          {data.reviewCount} voice-tracked item{data.reviewCount === 1 ? '' : 's'} still being checked by the coach.
        </div>
      )}

      {!hasAny && !editing && !editor && (
        <div className="px-3 py-4 text-sm" style={{ color: 'var(--text-2)' }}>
          No stats recorded for this game yet. Use <strong>Track live</strong> on the bench,
          or <strong>Enter stats</strong> afterwards from the scoresheet.
        </div>
      )}

      {/* 2026-10-22 (per Gilles) — the NHL box score: every player, every
          stat, one row each (same view as the Stats tab and the family
          page). The grouped tables with per-cell inputs stay for Enter
          stats (editing) only. */}
      {hasAny && !editing && !editor && <NhlBoxScore data={data} teamId={teamId} gameId={gameId} canEdit={canEdit} onChanged={load} />}

      {editing && !editor && groups.map(({ group, stats }) => (
        <div key={group}>
          <div className="px-3 pt-3 pb-1 text-xs font-bold"
            style={{ color: 'var(--ice)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            {GROUP_LABEL[group] || group}
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  <th style={{ ...headCell, textAlign: 'left', width: 34 }}>#</th>
                  <th style={{ ...headCell, textAlign: 'left' }}>Player</th>
                  {stats.map(s => <th key={s.code} style={headCell} title={s.label}>{s.short}</th>)}
                  {group === 'faceoffs' && <th style={headCell}>FO%</th>}
                  {group === 'scoring' && <th style={headCell}>PTS</th>}
                </tr>
              </thead>
              <tbody>
                {players.map(p => (
                  <tr key={p.userId} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ ...cell, textAlign: 'left', color: 'var(--text-3)' }}>
                      {p.jerseyNumber ?? ''}
                    </td>
                    <td style={{ ...cell, textAlign: 'left', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 150 }}>
                      {p.name || 'Unknown'}
                      {p.offRoster && <span style={{ color: 'var(--text-3)', fontSize: 11 }}> (off roster)</span>}
                    </td>
                    {stats.map(s => (
                      <td key={s.code} style={cell}>
                        {editing ? (
                          <input
                            type="number" min="0" max="1000" inputMode="numeric"
                            value={draft[p.userId]?.[s.code] ?? 0}
                            onChange={e => setDraft(d => ({
                              ...d,
                              [p.userId]: { ...(d[p.userId] || {}), [s.code]: e.target.value === '' ? 0 : Math.max(0, parseInt(e.target.value, 10) || 0) },
                            }))}
                            style={{
                              width: 48, padding: '2px 4px', fontSize: 13, textAlign: 'right',
                              background: 'var(--surface)', border: '1px solid var(--border)',
                              borderRadius: 4, color: 'var(--text)',
                            }} />
                        ) : (p.stats?.[s.code] || 0)}
                      </td>
                    ))}
                    {group === 'faceoffs' && (
                      <td style={{ ...cell, color: 'var(--text-2)' }}>
                        {p.derived?.faceoffPct != null ? `${p.derived.faceoffPct}%` : '—'}
                      </td>
                    )}
                    {group === 'scoring' && (
                      <td style={{ ...cell, fontWeight: 700 }}>{p.derived?.points ?? 0}</td>
                    )}
                  </tr>
                ))}
                <tr style={{ background: 'var(--surface-3, rgba(255,255,255,0.03))' }}>
                  <td style={{ ...cell, textAlign: 'left' }} />
                  <td style={{ ...cell, textAlign: 'left', fontWeight: 700 }}>Total</td>
                  {stats.map(s => (
                    <td key={s.code} style={{ ...cell, fontWeight: 700 }}>
                      {data.team?.total?.stats?.[s.code] || 0}
                    </td>
                  ))}
                  {group === 'faceoffs' && (
                    <td style={{ ...cell, fontWeight: 700 }}>
                      {data.team?.total?.derived?.faceoffPct != null ? `${data.team.total.derived.faceoffPct}%` : '—'}
                    </td>
                  )}
                  {group === 'scoring' && (
                    <td style={{ ...cell, fontWeight: 700 }}>{data.team?.total?.derived?.points ?? 0}</td>
                  )}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {hasAny && (
        <div className="px-3 py-2 text-xs" style={{ color: 'var(--text-3)', borderTop: '1px solid var(--border)' }}>
          {data.eventCount} event{data.eventCount === 1 ? '' : 's'} recorded
        </div>
      )}
    </div>
  );
}


/** The one-table NHL box score, built from the game's current events. */
const POS_L = { forward: 'F', centre: 'F', center: 'F', wing: 'F', defense: 'D', defence: 'D', goalie: 'G' };
export function NhlBoxScore({ data, teamId = null, gameId = null, canEdit = false, onChanged = null }) {
  // 2026-10-23 (per Gilles) - tap a count to set it by hand; percentages and
  // P stay locked (they are worked out from the counts). The Unknown row
  // can also MOVE counts onto a real player, words and clip included.
  const [cellEdit, setCellEdit] = useState(null);   // { row, statKey, fo }
  const [adding, setAdding] = useState(false);
  const { skaters, goalies } = useMemo(() => {
    const events = data?.events || [];
    const roster = data?.roster || [];
    const byNum = new Map(roster.filter(r => r.jerseyNumber != null).map(r => [String(r.jerseyNumber), r]));
    const byId = new Map(roster.map(r => [r.userId, r]).filter(([k]) => k));
    const rows = new Map();
    const get = (k, meta) => { if (!rows.has(k)) rows.set(k, { ...meta, g: 0, a: 0, sog: 0, ms: 0, bs: 0, fow: 0, fol: 0, pim: 0, sv: 0, ga: 0 }); return rows.get(k); };
    // 2026-10-23 (per Gilles) - EVERYONE on the roster shows, zeros and all
    for (const r0 of roster) {
      const k0 = r0.userId ? 'u' + r0.userId : (r0.jerseyNumber != null ? 'n' + r0.jerseyNumber : null);
      if (k0) get(k0, { key: k0, number: r0.jerseyNumber, name: r0.name, pos: POS_L[String(r0.position || '').toLowerCase()] || null });
    }
    for (const e of events) {
      let meta;
      if (e.user_id && byId.has(e.user_id)) { const r = byId.get(e.user_id); meta = { key: 'u' + e.user_id, number: r.jerseyNumber, name: r.name, pos: POS_L[String(r.position || '').toLowerCase()] || null }; }
      else if (e.jersey_number != null && byNum.has(String(e.jersey_number))) { const r = byNum.get(String(e.jersey_number)); meta = { key: 'n' + e.jersey_number, number: r.jerseyNumber, name: r.name, pos: POS_L[String(r.position || '').toLowerCase()] || null }; }
      else meta = { key: 'unknown', number: null, name: 'Unknown', pos: null };
      const r = get(meta.key, meta);
      const n = Number(e.value) || 1;
      const SK = { goal: 'g', assist: 'a', shot: 'sog', shot_missed: 'ms', shot_blocked: 'bs',
                   faceoff_win: 'fo', faceoff_loss: 'fo', penalty_minutes: 'pim', save: 'sv' }[e.event_type];
      if (SK && !(e.event_type === 'goal' && e.team_side === 'away')) {
        r._ev = r._ev || {};
        (r._ev[SK] = r._ev[SK] || []).push(e);
      }
      switch (e.event_type) {
        case 'goal': if (e.team_side !== 'away') r.g += n; break;
        case 'assist': r.a += n; break;
        case 'shot': r.sog += n; break;
        case 'shot_missed': r.ms += n; break;
        case 'shot_blocked': r.bs += n; break;
        case 'faceoff_win': r.fow += n; break;
        case 'faceoff_loss': r.fol += n; break;
        case 'penalty_minutes': r.pim += n; break;
        case 'save': r.sv += n; break;
        case 'goal_against': if (e.engine_key) r.ga += n; break;
        default: break;
      }
    }
    const sk = []; const gl = [];
    for (const r of rows.values()) {
      const row = { ...r, label: r.name === 'Unknown' ? 'Unknown' : `${r.number != null ? '#' + r.number + ' ' : ''}${r.name || ''}`.trim(), p: r.g + r.a };
      if (r.pos === 'G' || (r.name === 'Unknown' && (r.sv || r.ga))) gl.push({ ...row, svPct: r.sv + r.ga > 0 ? Math.round((r.sv / (r.sv + r.ga)) * 1000) / 1000 : null });
      if (r.pos !== 'G' || row.g || row.a || row.sog || row.pim) { if (!(r.name === 'Unknown' && !row.g && !row.a && !row.sog && !row.ms && !row.bs && !row.fow && !row.fol && !row.pim)) sk.push(row); }
    }
    const num = (x) => (x.number != null ? Number(x.number) : 999);
    sk.sort((a, b) => (a.label === 'Unknown') - (b.label === 'Unknown') || num(a) - num(b));
    gl.sort((a, b) => num(a) - num(b));
    return { skaters: sk, goalies: gl };
  }, [data]);
  if (!skaters.length && !goalies.length) return null;
  const cols = [
    { key: 'label', label: 'Player', left: true, bold: true, render: (r) => (<>{r.label}{r.pos ? <span style={{ color: 'var(--text-3)', fontWeight: 500, marginLeft: 6, fontSize: '0.85em' }}>{r.pos}</span> : null}</>) },
    { key: 'g', label: 'G', title: 'Goals' }, { key: 'a', label: 'A', title: 'Assists' },
    { key: 'p', label: 'P', title: 'Points', bold: true }, { key: 'sog', label: 'SOG', title: 'Shots on goal' },
    { key: 'ms', label: 'MS', title: 'Missed shots', render: (r) => r.ms || '' },
    { key: 'bs', label: 'BS', title: 'Blocked shots', render: (r) => r.bs || '' },
    { key: 'fo', label: 'FO', title: 'Faceoffs won–lost', render: (r) => ((r.fow || 0) + (r.fol || 0) > 0 ? `${r.fow || 0}–${r.fol || 0}` : '') },
    { key: 'foPct', label: 'FO%', title: 'Faceoff win %', render: (r) => { const t = (r.fow || 0) + (r.fol || 0); return t > 0 ? `${Math.round(((r.fow || 0) / t) * 100)}%` : ''; } },
    { key: 'pim', label: 'PIM', title: 'Penalty minutes', render: (r) => r.pim || '' },
  ];
  const gcols = [
    { key: 'label', label: 'Goalie', left: true, bold: true },
    { key: 'sv', label: 'SV', title: 'Saves' }, { key: 'ga', label: 'GA', title: 'Goals against' },
    { key: 'svPct', label: 'SV%', render: (r) => (r.svPct == null ? '' : r.svPct.toFixed(3).replace(/^0/, '')) },
  ];
  // 2026-10-23 (per Gilles - "make it prettier, make it stand out"): the box
  // score dresses like the Stats page - zebra rows, a header band, the
  // number worn as a chip, points in ice-blue, goals and assists tinted.
  const headCell2 = { padding: '8px 10px', fontSize: 10.5, fontWeight: 800, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '0.07em', textAlign: 'right', background: 'var(--surface-2)', whiteSpace: 'nowrap' };
  const cell2 = { padding: '8px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' };
  const numChip = (r) => (
    <span className="inline-flex items-center gap-2">
      <span style={{ minWidth: 34, textAlign: 'center', fontFamily: 'var(--font-mono)', fontWeight: 800, fontSize: 11,
        color: 'var(--ice)', background: 'var(--ice-a8)', border: '1px solid var(--ice-a20, var(--border))', borderRadius: 7, padding: '2px 5px' }}>
        {r.number != null ? `#${r.number}` : '—'}
      </span>
      <span style={{ fontWeight: 700 }}>{r.name === 'Unknown' || r.label === 'Unknown' ? 'Unknown' : (r.name || String(r.label || '').replace(/^#\d+\s*/, ''))}</span>
      {r.pos && <span style={{ color: 'var(--text-3)', fontWeight: 600, fontSize: '0.8em' }} title={{ F: 'Forward', D: 'Defense', G: 'Goalie' }[r.pos]}>{r.pos}</span>}
    </span>
  );
  const EDITABLE = new Set(['g', 'a', 'sog', 'ms', 'bs', 'pim', 'sv', 'fo']);
  const statCell = (c, r) => {
    const v0 = c.render ? c.render(r) : (r[c.key] || (c.left ? r[c.key] : ''));
    let v = v0;
    if (c.key === 'p' && r.p > 0) v = <span style={{ color: 'var(--ice)', fontWeight: 800 }}>{v0}</span>;
    else if (c.key === 'g' && r.g > 0) v = <span style={{ color: 'var(--goal)', fontWeight: 800 }}>{v0}</span>;
    else if (c.key === 'a' && r.a > 0) v = <span style={{ color: 'var(--ice)', fontWeight: 700 }}>{v0}</span>;
    else if (c.key === 'svPct' && v0) v = <span style={{ fontWeight: 800 }}>{v0}</span>;
    if (!canEdit || !EDITABLE.has(c.key)) {
      if (canEdit && ['p', 'foPct', 'svPct', 'ga'].includes(c.key)) {
        return <span title={c.key === 'ga' ? 'Goals against come from the opponent goals on the tape' : 'Worked out from the counts — edit the counts, not this'} style={{ cursor: 'not-allowed' }}>{v}</span>;
      }
      return v;
    }
    return (
      <button type="button" onClick={() => setCellEdit({ row: r, statKey: c.key })}
        title="Tap to set this count by hand (your number sticks through AI re-runs)"
        style={{ background: 'none', border: '1px dashed transparent', borderRadius: 6, cursor: 'pointer', padding: '1px 5px',
          color: 'inherit', font: 'inherit', fontVariantNumeric: 'tabular-nums' }}
        onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--ice)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'transparent'; }}>
        {v === '' || v == null ? '—' : v}
      </button>
    );
  };
  const table = (tcols, rows2) => (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead><tr>
          {tcols.map(c => <th key={c.key} title={c.title} style={{ ...headCell2, textAlign: c.left ? 'left' : 'right' }}>{c.label}</th>)}
        </tr></thead>
        <tbody>
          {rows2.map((r, i) => (
            <tr key={r.key || i} style={{ borderBottom: '1px solid var(--border)', background: i % 2 ? 'var(--surface-2)' : 'transparent' }}>
              {tcols.map(c => <td key={c.key} style={{ ...cell2, textAlign: c.left ? 'left' : 'right', fontWeight: c.bold && !c.left ? 700 : 400 }}>
                {c.key === 'label' ? numChip(r) : statCell(c, r)}
              </td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
  return (
    <div data-nhl-box>
      <div className="px-3 pt-3 pb-2 text-sm font-bold flex items-center gap-2" style={{ color: 'var(--text)' }}>
        <span style={{ width: 4, height: 16, borderRadius: 2, background: 'var(--ice)', display: 'inline-block' }} />
        Player stats
        {canEdit && (
          <button type="button" onClick={() => setAdding(a => !a)}
            title="The narrator missed a stat? Add it by hand — pick the player, the stat, and how many"
            style={{ marginLeft: 'auto', padding: '2px 10px', fontSize: 11, fontWeight: 700, borderRadius: 999, cursor: 'pointer',
              background: adding ? 'var(--ice)' : 'var(--ice-a8)', color: adding ? '#fff' : 'var(--ice)', border: '1px solid var(--ice)' }}>
            {adding ? 'Close' : '+ Add a stat'}
          </button>
        )}
      </div>
      {adding && <AddStatPanel teamId={teamId} gameId={gameId} roster={data?.roster || []}
        onDone={() => { setAdding(false); onChanged?.(); }} />}
      {table(cols, skaters)}
      {goalies.length > 0 && <>
        <div className="px-3 pt-3 pb-2 text-sm font-bold flex items-center gap-2" style={{ color: 'var(--text)' }}>
          <span style={{ width: 4, height: 16, borderRadius: 2, background: 'var(--gold)', display: 'inline-block' }} />
          Goalie
        </div>
        {table(gcols, goalies)}
      </>}
      {cellEdit && (
        <StatCellEditor teamId={teamId} gameId={gameId} cell={cellEdit} roster={data?.roster || []}
          events={(cellEdit.row._ev && cellEdit.row._ev[cellEdit.statKey]) || []}
          onClose={() => setCellEdit(null)}
          onDone={() => { setCellEdit(null); onChanged?.(); }} />
      )}
    </div>
  );
}

/** The little editor behind a tapped count: set it, or (Unknown) move it. */
function StatCellEditor({ teamId, gameId, cell, roster, events = [], onClose, onDone }) {
  const [fixingEv, setFixingEv] = useState(null);
  const r = cell.row;
  const isUnknown = r.name === 'Unknown' || r.label === 'Unknown';
  const target = isUnknown ? { unknown: true }
    : (/^[0-9a-f-]{36}$/i.test(String(r.key || '').slice(1)) && String(r.key).startsWith('u'))
      ? { userId: String(r.key).slice(1) } : { jersey: r.number };
  const foMode = cell.statKey === 'fo';
  const [vals, setVals] = useState(foMode ? { fow: String(r.fow || 0), fol: String(r.fol || 0) } : { one: String(r[cell.statKey] || 0) });
  const [moveN, setMoveN] = useState('1');
  const [moveTo, setMoveTo] = useState('');
  const [busy, setBusy] = useState(false);
  const statLabel = { g: 'goals', a: 'assists', sog: 'shots on goal', ms: 'missed shots', bs: 'blocked shots', pim: 'penalty minutes', sv: 'saves', fo: 'faceoffs' }[cell.statKey];
  const post = (body) => api.post(`/teams/${teamId}/press-box/games/${gameId}/stat`, body);
  const save = async () => {
    setBusy(true);
    try {
      if (foMode) {
        await post({ stat: 'fow', target, set: Math.max(0, parseInt(vals.fow, 10) || 0) });
        await post({ stat: 'fol', target, set: Math.max(0, parseInt(vals.fol, 10) || 0) });
      } else {
        await post({ stat: cell.statKey, target, set: Math.max(0, parseInt(vals.one, 10) || 0) });
      }
      toast.success('Saved — your count sticks, even when the AI re-reads the game');
      onDone();
    } catch (e) { toast.error(e.response?.data?.error || 'Could not save'); setBusy(false); }
  };
  const move = async () => {
    const p = roster.find(x => String(x.userId || `n${x.jerseyNumber}`) === moveTo);
    if (!p) return;
    setBusy(true);
    try {
      const stat = foMode ? 'fow' : cell.statKey;
      const { data: out } = await post({ stat, target: { unknown: true },
        move: { n: Math.max(1, parseInt(moveN, 10) || 1), ...(p.userId ? { userId: p.userId } : { jersey: p.jerseyNumber }) } });
      toast.success(`Moved ${out.moved} ${statLabel} to ${p.name || '#' + p.jerseyNumber} — the words and clips went with them`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.error || 'Could not move them'); setBusy(false); }
  };
  const inp = { width: 70, padding: '6px 8px', fontSize: 13, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)' };
  return (
    <div className="mx-3 my-2 p-3 rounded-lg" style={{ background: 'var(--surface-2)', border: '1px solid var(--ice)' }} data-stat-editor>
      <div className="text-sm font-bold mb-2">
        {isUnknown ? 'Unknown' : (r.name || r.label)} — {statLabel}
        <button type="button" className="btn-ghost" style={{ padding: '2px 8px', float: 'right' }} onClick={onClose}>Close</button>
      </div>
      <div className="flex items-center gap-2 mb-1" style={{ flexWrap: 'wrap' }}>
        {foMode ? (
          <>
            <label className="text-xs" style={{ color: 'var(--text-3)' }}>Won <input style={inp} type="number" min="0" value={vals.fow} onChange={(e) => setVals(v => ({ ...v, fow: e.target.value }))} /></label>
            <label className="text-xs" style={{ color: 'var(--text-3)' }}>Lost <input style={inp} type="number" min="0" value={vals.fol} onChange={(e) => setVals(v => ({ ...v, fol: e.target.value }))} /></label>
          </>
        ) : (
          <label className="text-xs" style={{ color: 'var(--text-3)' }}>Count <input style={inp} type="number" min="0" value={vals.one} onChange={(e) => setVals(v => ({ one: e.target.value }))} /></label>
        )}
        <button type="button" className="btn-ice" style={{ padding: '6px 12px' }} disabled={busy} onClick={save}>Save</button>
        <span className="text-xs" style={{ color: 'var(--text-3)' }}>FO%, SV% and P follow the counts by themselves.</span>
      </div>
      {events.length > 0 && (
        <div className="mt-2 pt-2" style={{ borderTop: '1px solid var(--border)' }}>
          <div className="text-xs font-bold mb-1" style={{ color: 'var(--text-2)' }}>The calls behind this number — listen, fix, or remove each one</div>
          {events.map((e2) => (
            <div key={e2.id} className="py-1.5 text-xs" style={{ borderBottom: '1px solid var(--border)' }}>
              <div className="flex items-center gap-2" style={{ flexWrap: 'wrap' }}>
                <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-3)', minWidth: 42 }}>
                  {e2.audio_offset_ms != null ? `${Math.floor(e2.audio_offset_ms / 60000)}:${String(Math.floor((e2.audio_offset_ms % 60000) / 1000)).padStart(2, '0')}` : '—'}
                </span>
                {e2.period && <span style={{ color: 'var(--text-3)' }}>P{e2.period}</span>}
                {e2.event_type === 'faceoff_loss' && <span style={{ color: 'var(--text-3)' }}>lost</span>}
                {e2.event_type === 'faceoff_win' && <span style={{ color: 'var(--text-3)' }}>won</span>}
                <ClipButton teamId={teamId} recordingId={e2.recording_id} offsetMs={e2.audio_offset_ms} />
                <button type="button" onClick={() => setFixingEv(fixingEv === e2.id ? null : e2.id)}
                  title="Assign this call to another player — quick-search the roster"
                  style={{ padding: '1px 9px', fontSize: 11, fontWeight: 700, borderRadius: 999, cursor: 'pointer',
                    background: 'var(--gold-a10, rgba(240,180,41,0.12))', color: 'var(--gold)', border: '1px solid var(--gold)' }}>
                  {fixingEv === e2.id ? 'Cancel' : 'Fix'}
                </button>
                {/* 2026-10-23 (per Gilles) — reclassify the call: a "shot"
                    that was really a save, and so on. */}
                <select value={e2.event_type} title="Change what this call WAS — shot, save, goal… (your pick sticks through AI re-runs)"
                  className="input-field" style={{ width: 'auto', padding: '2px 6px', fontSize: 11 }}
                  onChange={async (ev3) => {
                    try {
                      await api.patch(`/teams/${teamId}/game-events/${gameId}/${e2.id}`, { event_type: ev3.target.value });
                      toast.success('Reclassified'); onDone();
                    } catch (e4) { toast.error(e4.response?.data?.error || 'Could not change it'); }
                  }}>
                  {[['shot', 'Shot'], ['shot_missed', 'Missed shot'], ['shot_blocked', 'Blocked shot'], ['save', 'Save'],
                    ['goal', 'Goal'], ['assist', 'Assist'], ['faceoff_win', 'Faceoff won'], ['faceoff_loss', 'Faceoff lost']]
                    .map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <button type="button" onClick={async () => {
                  if (!window.confirm('Remove this call? It is remembered as "did not happen" and the AI never re-creates it.')) return;
                  try { await api.delete(`/teams/${teamId}/game-events/${gameId}/${e2.id}`); toast.success('Removed'); onDone(); }
                  catch (e3) { toast.error(e3.response?.data?.error || 'Could not remove it'); }
                }}
                  style={{ padding: '1px 9px', fontSize: 11, fontWeight: 700, borderRadius: 999, cursor: 'pointer',
                    background: 'var(--penalty-a10, rgba(255,59,59,0.1))', color: 'var(--penalty)', border: '1px solid var(--penalty)' }}>
                  Didn't happen
                </button>
              </div>
              {e2.transcript && <div style={{ color: 'var(--text-3)', fontStyle: 'italic', marginTop: 2 }}>&ldquo;{String(e2.transcript).slice(0, 160)}&rdquo;</div>}
              {fixingEv === e2.id && (
                <ReassignPicker teamId={teamId} gameId={gameId} eventId={e2.id} roster={roster}
                  current={null} onClose={() => setFixingEv(null)} onDone={onDone} />
              )}
            </div>
          ))}
        </div>
      )}
      {isUnknown && (
        <div className="flex items-center gap-2 mt-2 pt-2" style={{ borderTop: '1px solid var(--border)', flexWrap: 'wrap' }}>
          <span className="text-xs" style={{ color: 'var(--text-2)' }}>…or move</span>
          <input style={inp} type="number" min="1" value={moveN} onChange={(e) => setMoveN(e.target.value)} />
          <span className="text-xs" style={{ color: 'var(--text-2)' }}>of them to</span>
          <select className="input-field" style={{ width: 'auto', padding: '6px 8px', fontSize: 13 }} value={moveTo} onChange={(e) => setMoveTo(e.target.value)}>
            <option value="">Pick a player…</option>
            {roster.map(p2 => (
              <option key={p2.userId || p2.jerseyNumber} value={String(p2.userId || `n${p2.jerseyNumber}`)}>
                {p2.jerseyNumber != null ? `#${p2.jerseyNumber} ` : ''}{p2.name || ''}
              </option>
            ))}
          </select>
          <button type="button" className="btn-ghost" style={{ color: 'var(--gold)', border: '1px solid var(--gold)', borderRadius: 999, padding: '4px 12px' }}
            disabled={busy || !moveTo} onClick={move}>Move — the words and audio go with them</button>
        </div>
      )}
    </div>
  );
}


/** "+ Add a stat" — a stat the narrator never called. */
function AddStatPanel({ teamId, gameId, roster, onDone }) {
  const STATS = [['g', 'Goal'], ['a', 'Assist'], ['sog', 'Shot on goal'], ['ms', 'Missed shot'], ['bs', 'Blocked shot'],
    ['fow', 'Faceoff won'], ['fol', 'Faceoff lost'], ['pim', 'Penalty minutes'], ['sv', 'Save (goalie)']];
  const [who, setWho] = useState('');
  const [stat, setStat] = useState('g');
  const [n, setN] = useState('1');
  const [busy, setBusy] = useState(false);
  return (
    <div className="mx-3 mb-2 p-3 rounded-lg flex items-center gap-2" style={{ background: 'var(--surface-2)', border: '1px solid var(--ice)', flexWrap: 'wrap' }} data-add-stat>
      <select className="input-field" style={{ width: 'auto', padding: '6px 8px', fontSize: 13 }} value={who} onChange={(e) => setWho(e.target.value)}>
        <option value="">Who?</option>
        {roster.map(p => (
          <option key={p.userId || p.jerseyNumber} value={String(p.userId || `n${p.jerseyNumber}`)}>
            {p.jerseyNumber != null ? `#${p.jerseyNumber} ` : ''}{p.name || ''}
          </option>
        ))}
      </select>
      <select className="input-field" style={{ width: 'auto', padding: '6px 8px', fontSize: 13 }} value={stat} onChange={(e) => setStat(e.target.value)}>
        {STATS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </select>
      <input className="input-field" type="number" min="1" max="100" style={{ width: 70, padding: '6px 8px', fontSize: 13 }} value={n} onChange={(e) => setN(e.target.value)} />
      <button type="button" className="btn-ice" style={{ padding: '6px 12px' }} disabled={busy || !who}
        onClick={async () => {
          const p = roster.find(x => String(x.userId || `n${x.jerseyNumber}`) === who);
          if (!p) return;
          setBusy(true);
          try {
            await api.post(`/teams/${teamId}/press-box/games/${gameId}/stat`, {
              stat, target: p.userId ? { userId: p.userId } : { jersey: p.jerseyNumber },
              add: Math.max(1, parseInt(n, 10) || 1) });
            toast.success('Added — hand stats stick through AI re-runs');
            onDone();
          } catch (e) { toast.error(e.response?.data?.error || 'Could not add it'); setBusy(false); }
        }}>Add</button>
      <span className="text-xs" style={{ color: 'var(--text-3)' }}>e.g. an assist the narrator never called.</span>
    </div>
  );
}
